import Linkify from 'linkify-react'
import { useMemo } from 'react'
import { TbExternalLink } from 'react-icons/tb'
import { PaginatedCommentSingleComment } from '../types'

export function CommentContent({ comment }: { comment: PaginatedCommentSingleComment }) {
  // Backend converts {{userId}} to @FirstName LastName
  // We parse the @Name mentions and add styling
  const content = useMemo(() => {
    const parts: (string | JSX.Element)[] = []
    const text = comment.parsedText
    // Match @FirstName LastName patterns (backend converts {{userId}} to this exact format)
    // Use lookahead to ensure we stop after the last name (don't match the rest of the message)
    const mentionRegex = /@(\w+\s+\w+)(?=\s|[^\w]|$)/g
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = mentionRegex.exec(text)) !== null) {
      // Add text before the mention
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index))
      }
      // Add styled mention
      parts.push(
        <span key={match.index} className="rounded-sm bg-blue-1 p-1 text-blue-9">
          @{match[1]}
        </span>,
      )
      lastIndex = match.index + match[0].length
    }
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex))
    }

    return parts.length > 0 ? parts : text
  }, [comment.parsedText])

  return (
    <Linkify
      options={{
        render: {
          url: ({ attributes, content }) => {
            const { href, ...props } = attributes
            return (
              <a
                className="inline-flex items-center gap-0.5 whitespace-nowrap text-gray-11 hover:text-blue-9 hover:underline"
                href={href}
                rel="noreferrer"
                target="_blank"
                {...props}
              >
                {content} <TbExternalLink />
              </a>
            )
          },
        },
      }}
    >
      {content}
    </Linkify>
  )
}
